/**CORE LIBRARIES IMPORT */
import React from "react";

/**COMPONENTS IMPORT */
import PartnerLockup from "./PartnerLockup";
import TurkishFlag from "./TurkishFlag";

/**DATA IMPORT */
import { formatEventDate, formatPrice } from "../data/events";

/**
 * Event poster in the NeonPulse style.
 * - If `event.poster.image` is set (uploaded via admin), that image is shown.
 * - Otherwise the poster is composed from event data. The historical portrait
 *   is an image slot: it shows only an owner-supplied, licensed photograph
 *   and is never generated. Without one, a clearly labelled slot is shown.
 */
const EventPoster = ({ event }) => {
  const poster = event.poster || {};

  if (poster.image?.src) {
    return (
      <figure className="event-poster event-poster-image">
        <div className="img-wrapper">
          <img src={poster.image.src} alt={poster.image.alt || `${event.title} poster`} loading="lazy" decoding="async" />
        </div>
      </figure>
    );
  }

  const portrait = poster.portrait || {};
  const priceLine = `${formatPrice(event.price)} ${event.price.basis}: food menu, drinks excluded`;

  return (
    <figure className="event-poster" aria-label={`${event.title} poster`}>
      <div className="event-poster-frame">
        <div className="event-poster-top">
          <TurkishFlag className="event-poster-flag" />
          {poster.headline && (
            <p className="event-poster-headline" lang="tr">
              {poster.headline}
            </p>
          )}
        </div>

        <div className="event-poster-portrait">
          {portrait.src ? (
            <>
              <img src={portrait.src} alt={portrait.alt} loading="lazy" decoding="async" />
              {(portrait.credit || portrait.license) && (
                <figcaption className="event-poster-credit">
                  {[portrait.credit, portrait.license].filter(Boolean).join(" · ")}
                </figcaption>
              )}
            </>
          ) : (
            <div className="event-poster-slot" role="img" aria-label="Image slot: portrait to be supplied">
              <span className="photo-placeholder-tag">Image slot</span>
              <span className="photo-placeholder-label">
                Authentic, licensed historical portrait of Mustafa Kemal Atatürk to be supplied
              </span>
            </div>
          )}
        </div>

        <div className="event-poster-body">
          <h3 className="event-poster-title">{event.title}</h3>
          <p className="event-poster-when">
            <span>{formatEventDate(event.date, { weekday: undefined })}</span>
            {event.startTime && <span> · {event.startTime}</span>}
          </p>
          {poster.tagline && <p className="event-poster-tagline">{poster.tagline}</p>}
          <p className="event-poster-price">{priceLine}</p>
          {event.partners && <PartnerLockup partners={event.partners} label={event.presentedBy} />}
        </div>
      </div>
    </figure>
  );
};

export default EventPoster;
